import React, { useState, useEffect } from 'react';
import {
    SimpleShowLayout,
    TextField,
    ReferenceField,
    SelectField,
    NumberField,
    DateField,
    FunctionField,
    ArrayField,
    Datagrid,
    ReferenceArrayField,
    SingleFieldList,
    ImageField,
    usePermissions,
    useDataProvider,
    useLocaleState,
    useTranslate,
} from 'react-admin';
import { Grid, Box, Typography, Collapse, useTheme, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { useDrawer } from '@/composables/useDrawer';
import getLoadingState from '@/helpers/ui/getLoadingState.jsx';
import { CustomButton } from '@/components/global/buttons/CustomButton';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import CustomCollapseButton from '@/components/global/buttons/CustomCollapseButton.jsx';
import CustomChipField from '@/components/global/fields/CustomChipField.jsx';
import { NoImage } from '@/components/global/fields/NoImage';
import UnassignedField from '@/components/global/fields/UnassignedField.jsx';
import { boolChoices, productColorChoices, productStatusChoices } from '@/components/global/options/selectOptions.jsx';
import PackageIcon from '@/assets/icons/products-collected-icon.svg?react';
import TransactionsCountIcon from '@/assets/icons/transactions-count-icon.svg?react';
import DepositIcon from '@/assets/icons/deposit-icon.svg?react';
import EditIcon from '@/assets/icons/edit-icon.svg?react';
import MarketShareBar from '@/components/product/MarketShareBar.jsx';
import { NoDataPlaceholder } from '@/components/analytics/common/NoDataPlaceholder.jsx';
import { Palette } from '@/themes/themeOverrides/Palette';
import { ProductHistory } from '@/components/product/ProductHistory.jsx';
import { CustomDatagridBody } from '@/components/global/list/CustomDatagridRow.jsx';

const StyledProductDetails = styled(Grid)(() => ({
    '& .product-details-wrapper': {
        '& .popup-box.popup-box--header': {
            '& .popup-box--header-title': {
                fontSize: '22px',
                marginBottom: '0',
            },
        },
    },
}));

export const ProductDetails = ({ handleClose, elementId }) => {
    const translate = useTranslate();
    const [currentLocale] = useLocaleState();
    const { permissions } = usePermissions();
    const dataProvider = useDataProvider();
    const [product, setProduct] = useState(null);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(true);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { openDrawer } = useDrawer();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const handleToggle = () => {
        setOpen(prev => !prev);
    };
    const loadingState = getLoadingState({ isLoading, error, data: product, translate });
    const transactionsFilter = { 'transactionContainers->product->id_in': [elementId] };
    const transactionsLink = `/transaction?filter=${JSON.stringify(transactionsFilter)}`;
    const categoryNameSource = currentLocale?.toLowerCase().startsWith('en') ? 'category.nameEn' : 'category.name';
    const tagNameSource = currentLocale?.toLowerCase().startsWith('en') ? 'nameEn' : 'name';
    const hasAnalytics = Boolean(product?.analytics?.[0]);

    useEffect(() => {
        if (elementId) {
            setIsLoading(true);
            dataProvider
                .getOne('product', { id: elementId })
                .then(({ data }) => {
                    setProduct(data);
                    setError(null);
                })
                .catch(err => {
                    console.error('Error fetching product:', err);
                    setError(err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [dataProvider, elementId]);

    if (loadingState) return loadingState;

    return (
        <StyledProductDetails>
            <Box sx={{ width: '100%', padding: '0px' }}>
                <Box className='popup-wrapper popup-wrapper-top'>
                    <Box className='popup-box popup-box--header'>
                        <Typography variant='h4'>{translate('resources.product.labels.productInfo')}</Typography>
                        <Box>
                            {permissions.isAdmin && (
                                <CustomButton
                                    color='secondary'
                                    label={translate('common.edit')}
                                    variant='contained'
                                    size={isDesktop ? 'large' : 'small'}
                                    startIcon={<EditIcon />}
                                    onClick={() => openDrawer(elementId, 'product-edit')}
                                />
                            )}
                            <CustomCloseButton onClose={handleClose} />
                        </Box>
                    </Box>
                </Box>
                <Box className='popup-wrapper'>
                    <Box className='popup-box popup-box--image'>
                        <SimpleShowLayout className='image' record={product}>
                            {product.pictureUrl ? <ImageField source='pictureUrl' /> : <NoImage sx={{ minWidth: '176px', height: '176px' }} />}
                        </SimpleShowLayout>
                        <Box className='title'>
                            <SimpleShowLayout className='name' record={product}>
                                <TextField source='name' />
                            </SimpleShowLayout>
                            <SimpleShowLayout record={product}>
                                <TextField source='ean' label={translate('resources.product.fields.ean')} />
                            </SimpleShowLayout>
                        </Box>
                    </Box>
                    {!(permissions.isService || permissions.isServiceMaster) ? (
                        <Box className='popup-box popup-box--statistics'>
                            <Link to={transactionsLink} className='statistics-box'>
                                <SimpleShowLayout record={product}>
                                    <PackageIcon />
                                    {hasAnalytics ? (
                                        <NumberField source='analytics[0].packagesCount' label={translate('resources.product.fields.totalCollected')} />
                                    ) : (
                                        <FunctionField
                                            label={translate('resources.product.fields.totalCollected')}
                                            render={() => <NoDataPlaceholder backgroundColor={Palette.gray.gray150} />}
                                        />
                                    )}
                                </SimpleShowLayout>
                            </Link>
                            <SimpleShowLayout className='statistics-box' record={product}>
                                <MarketShareBar record={product} />
                                <FunctionField
                                    label={translate('resources.product.fields.marketShare')}
                                    render={record =>
                                        hasAnalytics ? (
                                            `${Number(record.analytics[0]?.marketShare * 100).toFixed(2)}%`
                                        ) : (
                                            <NoDataPlaceholder backgroundColor={Palette.gray.gray150} />
                                        )
                                    }
                                />
                            </SimpleShowLayout>
                            <Link to={transactionsLink} className='statistics-box'>
                                <SimpleShowLayout record={product}>
                                    <TransactionsCountIcon />
                                    {hasAnalytics ? (
                                        <NumberField
                                            source='analytics[0].transactionsCount'
                                            label={translate('resources.product.fields.detailsTransactionsCount')}
                                        />
                                    ) : (
                                        <FunctionField
                                            label={translate('resources.product.fields.detailsTransactionsCount')}
                                            render={() => <NoDataPlaceholder backgroundColor={Palette.gray.gray150} />}
                                        />
                                    )}
                                </SimpleShowLayout>
                            </Link>
                            <SimpleShowLayout className='statistics-box' record={product}>
                                <DepositIcon />
                                <FunctionField
                                    label={translate('resources.collectionPoint.labels.depositSum')}
                                    render={record =>
                                        hasAnalytics ? (
                                            `${Number(record.analytics[0]?.transactionsCashDepositSum).toFixed(2)} ${record.currency}`
                                        ) : (
                                            <NoDataPlaceholder backgroundColor={Palette.gray.gray150} />
                                        )
                                    }
                                />
                            </SimpleShowLayout>
                        </Box>
                    ) : null}
                    <Box className={`popup-box ${product?.tagsByCategory?.length ? 'popup-box--categories' : 'popup-box--no-categories'}`}>
                        <SimpleShowLayout record={product}>
                            <ArrayField source='tagsByCategory'>
                                <Datagrid
                                    body={<CustomDatagridBody />}
                                    bulkActionButtons={false}
                                    rowClick={(id, resource, record) => `/tag-category/#${record.category.id}?tag-category-edit`}
                                >
                                    <TextField source={categoryNameSource} label={translate('common.category')} />
                                    <ReferenceArrayField
                                        source='tagIds'
                                        reference='tag'
                                        label={translate('resources.product.fields.tags')}
                                        sort={{ field: 'sortOrder', order: 'ASC' }}
                                        className='tag-list'
                                    >
                                        <SingleFieldList linkType={false}>
                                            <CustomChipField source={tagNameSource} />
                                        </SingleFieldList>
                                    </ReferenceArrayField>
                                </Datagrid>
                            </ArrayField>
                        </SimpleShowLayout>
                    </Box>
                </Box>
                <Box className='popup-wrapper popup-wrapper-top product-details-wrapper'>
                    <Box className='popup-box popup-box--header'>
                        <Typography variant='h5' className='popup-box--header-title'>
                            {translate('common.details')}
                        </Typography>
                        <Box>
                            <CustomCollapseButton isOpen={open} onToggle={handleToggle} />
                        </Box>
                    </Box>
                    <Collapse in={open}>
                        <Box className='product-details'>
                            <Box className='column'>
                                <SimpleShowLayout record={product}>
                                    <TextField source='shortenedName' label={translate('resources.product.fields.shortenedName')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <TextField source='shortenedNameAuto' label={translate('resources.product.fields.shortenedNameGpt')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <ReferenceField source='materialId' reference='material' label={translate('resources.product.fields.material')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <SelectField
                                        source='materialColor'
                                        choices={productColorChoices(translate)}
                                        label={translate('resources.product.fields.materialColor')}
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <SelectField
                                        source='capColor'
                                        choices={productColorChoices(translate)}
                                        label={translate('resources.product.fields.capColor')}
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <SelectField source='reusable' choices={boolChoices(translate)} label={translate('resources.product.fields.reusable')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <FunctionField
                                        label={translate('resources.product.fields.weight')}
                                        render={record => (record.totalWeight != null ? `${Number(record.totalWeight)} gram` : '')}
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <FunctionField
                                        label={translate('resources.product.fields.noCupWeight')}
                                        render={record => (record.noCupWeight != null ? `${Number(record.noCupWeight)} gram` : '')}
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <FunctionField
                                        label={translate('resources.product.fields.capacity')}
                                        render={record => (record.volume != null ? `${Number(record.volume)} ml` : '')}
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <FunctionField
                                        label={translate('resources.product.fields.height')}
                                        render={record => (record.height != null ? `${Number(record.height)} mm` : '')}
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <FunctionField
                                        label={translate('resources.product.fields.width')}
                                        render={record => (record.width != null ? `${Number(record.width)} mm` : '')}
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <FunctionField
                                        label={translate('resources.product.fields.thickness')}
                                        render={record => (record.thickness != null ? `${Number(record.thickness)} mm` : '')}
                                    />
                                </SimpleShowLayout>
                            </Box>
                            <Box className='column'>
                                <SimpleShowLayout record={product}>
                                    <TextField source='brand' label={translate('resources.product.fields.brand')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <SelectField
                                        source='ownerContractor'
                                        choices={boolChoices(translate)}
                                        label={translate('resources.product.fields.brandContractor')}
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <SelectField source='status' choices={productStatusChoices(translate)} label={translate('common.status')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <TextField source='gln' label={translate('resources.product.fields.gln')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <TextField source='gpc' label={translate('resources.product.fields.gpc')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <TextField source='source' label={translate('resources.product.fields.source')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <TextField source='operatorId' label={translate('common.operator')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <FunctionField
                                        label={translate('resources.product.fields.deposit')}
                                        render={record => (record.deposit != null ? `${Number(record.deposit)} pln` : '')}
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <DateField source='operatorStartDate' label={translate('resources.product.fields.productIntroductionDate')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <DateField source='operatorEndDate' label={translate('resources.product.fields.productWithdrawalDate')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <FunctionField
                                        label={translate('resources.product.fields.productWithdrawalDate2')}
                                        render={record =>
                                            record.marketWithdrawalDate ? (
                                                <DateField source='marketWithdrawalDate' record={record} />
                                            ) : (
                                                <UnassignedField label={translate('resources.product.labels.notApplicable')} />
                                            )
                                        }
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout record={product}>
                                    <DateField source='updatedAt' label={translate('resources.product.fields.lastUpdate')} />
                                </SimpleShowLayout>
                                {permissions.isAdmin && (
                                    <SimpleShowLayout record={product}>
                                        <DateField source='lastCbdokUpdate' label={translate('resources.product.fields.lastCbdokUpdate')} />
                                    </SimpleShowLayout>
                                )}
                            </Box>
                        </Box>
                    </Collapse>
                </Box>
                {permissions.isAdmin && (
                    <Box className='popup-wrapper popup-wrapper-top product-details-wrapper' sx={{ borderTop: '2px solid #F2F2F2' }}>
                        <Box className='popup-box popup-box--header'>
                            <Typography variant='h5' className='popup-box--header-title'>
                                {translate('resources.product.history.title')}
                            </Typography>
                            <Box>
                                <CustomCollapseButton isOpen={historyOpen} onToggle={() => setHistoryOpen(prev => !prev)} />
                            </Box>
                        </Box>
                        <Collapse in={historyOpen}>
                            <ProductHistory productId={elementId} />
                        </Collapse>
                    </Box>
                )}
            </Box>
        </StyledProductDetails>
    );
};
